import {CorePlugin, SupportedVersion} from '@clappr/core'
import {IFrameProvider} from 'common/types/provider'
import {LoyaltyConfig, AdvertisementBlockType} from 'common/api/records'
import {Events} from 'common/types/events'
import {Options} from 'common/types/options'
import {Viewport} from 'common/plugins/viewport'
import {Share} from 'common/plugins/share'
import {Advertisement, AdvertisementEvent} from 'common/plugins/advertisement'

// use runtime symbols to prevent replacing keys by extensions
const ON_VISIBLE_AREA_CHANGED: unique symbol = Symbol('onVisibleAreaChanged')

export class Loyalty<T extends Options> extends CorePlugin<T> {
  public static VIDEO_START = 'video_start'
  public static VIDEO_FINISHED = 'video_finished'
  public static VIDEO_PREROLL = 'video_preroll'
  public static VIDEO_MIDDLEROLL = 'video_middleroll'
  public static VIDEO_PAUSEROLL = 'video_pauseroll'
  public static VIDEO_PAUSEBANNER = 'video_pausebanner'
  public static VIDEO_POSTROLL = 'video_postroll'
  public static VIDEO_PSEUDOSTREAMING = 'video_pseudostreaming'
  public static VIDEO_SHARE = 'video_share'

  public static ADVERTISEMENT_EVENTS = {
    [AdvertisementBlockType.PRE_ROLL]: Loyalty.VIDEO_PREROLL,
    [AdvertisementBlockType.PRE_ROLL_MOBILE]: Loyalty.VIDEO_PREROLL,
    [AdvertisementBlockType.MIDDLE_ROLL]: Loyalty.VIDEO_MIDDLEROLL,
    [AdvertisementBlockType.MIDDLE_ROLL_MOBILE]: Loyalty.VIDEO_MIDDLEROLL,
    [AdvertisementBlockType.PAUSE_ROLL]: Loyalty.VIDEO_PAUSEROLL,
    [AdvertisementBlockType.PAUSE_ROLL_MOBILE]: Loyalty.VIDEO_PAUSEROLL,
    [AdvertisementBlockType.POST_ROLL]: Loyalty.VIDEO_POSTROLL,
    [AdvertisementBlockType.POST_ROLL_MOBILE]: Loyalty.VIDEO_POSTROLL,
    [AdvertisementBlockType.PAUSE_BANNER]: Loyalty.VIDEO_PAUSEBANNER,
    [AdvertisementBlockType.PAUSE_BANNER_MOBILE]: Loyalty.VIDEO_PAUSEBANNER,
    [AdvertisementBlockType.BANNER]: undefined,
    [AdvertisementBlockType.BANNER_MOBILE]: undefined,
    [AdvertisementBlockType.PSEUDO_STREAMING]: Loyalty.VIDEO_PSEUDOSTREAMING,
    [AdvertisementBlockType.PSEUDO_STREAMING_MOBILE]:
      Loyalty.VIDEO_PSEUDOSTREAMING
  }

  private static MIN_ACTIVE_VISIBLE_HEIGHT = 0.8

  private started = false
  private visible = false

  public get name(): string {
    return 'loyalty'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  private get provider(): IFrameProvider | undefined {
    return this.core.options.provider
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  private get share(): Share<T> | null {
    return this.core.getPlugin('share') as Share<T> | null
  }

  private get viewport(): Viewport<T> | null {
    return this.core.getPlugin('viewport') as Viewport<T> | null
  }

  public get params(): LoyaltyConfig {
    return this.core.options.loyalty || ({} as LoyaltyConfig)
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(
        activeContainer,
        Events.CONTAINER_TIMEUPDATE,
        this.onTimeUpdate
      )
      this.listenTo(activeContainer, Events.CONTAINER_ENDED, this.onEnded)
    }

    if (this.advertisement) {
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_ENDED,
        this.onAdEnded
      )
    }

    if (this.share) {
      this.listenTo(this.share, Events.SHARE_SOCIALNETWORK, this.onShare)
    }

    if (this.viewport) {
      this.listenTo(
        this.viewport,
        Events.VIEWPORT_VISIBLE_AREA_CHANGED,
        this[ON_VISIBLE_AREA_CHANGED]
      )
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private [ON_VISIBLE_AREA_CHANGED](visibleHeight: number): void {
    this.visible = visibleHeight >= Loyalty.MIN_ACTIVE_VISIBLE_HEIGHT
  }

  private onTimeUpdate(): void {
    const isPlaying = this.core.activeContainer?.isPlaying()

    if (!this.started && isPlaying) {
      this.send(Loyalty.VIDEO_START)
      this.started = true
    }
  }

  private onEnded(): void {
    this.send(Loyalty.VIDEO_FINISHED)
    this.started = false
  }

  private onAdEnded(event: AdvertisementEvent): void {
    const eventType = Loyalty.ADVERTISEMENT_EVENTS[event.blockType]

    if (eventType) {
      this.send(eventType)
    }
  }

  private onShare(): void {
    this.send(Loyalty.VIDEO_SHARE)
  }

  private send(eventType: string): void {
    if (this.visible) {
      const {project} = this.params
      const {userId} = this.core.options

      this.provider?.loyalty(eventType, {project, ...(userId && {userId})})
    }
  }
}
